/**
 * Room and exit schemas — the traversable world (plan §3, amended R1.1).
 *
 * @remarks
 * Rooms carry the room half of narrative v0: `description` is the repeatable
 * LOOK/entry text on the `environment` channel, and `firstVisitNarratives`
 * (R1.1) are the one-shot arrival beats — the POC design fires those on two
 * or three channels per room, which a single string cannot say. Exits own the
 * v0 lock model. Whether `directionToken` matches an authored vocabulary
 * direction is a semantic cross-check — `content-loader`'s job, not this
 * schema's (deferred from R3; `exit-word-rules.ts` does it since D230).
 *
 * **v0 lock semantics (ruled 2026-08-12, R1.1):** an exit with a lock is
 * refused iff its `exitId` is NOT in `WorldState.unlockedExitIds` AND
 * (`lockedFlag` is absent ? always : the flag's value is `true`).
 * `unlockedExitIds` — fed by the `unlock_exit` effect — always wins, so a
 * lock WITHOUT a `lockedFlag` is simply "locked until something unlocks it,"
 * and a lock WITH one is flag-driven (a `lockedFlag` that starts `true` and
 * is never cleared makes a permanently sealed exit). TRUE means LOCKED.
 *
 * @packageDocumentation
 */

import { z } from 'zod';

import { conditionalAmbienceSchema } from './audio.js';
import { conditionalNarrativesSchema } from './conditional-narratives.js';
import { conditionalTextSchema, roomDescriptionFragmentSchema } from './conditional-text.js';
import { conditionSchema } from './conditions.js';
import { exitIdSchema, itemIdSchema, roomIdSchema, spriteIdSchema } from './identifiers.js';
import { conditionalImageSchema } from './images.js';
import { nonBlankStringSchema } from './primitives.js';
import { spokenWhenSchema } from './spoken-when.js';
import { topicsSchema } from './topics.js';
import { narrativeLineSchema } from './transcript.js';

/**
 * One exit's lock (D156): what holds it shut, and what it says when it does.
 *
 * @remarks
 * **`lockedWhen` is a CONDITION, and that is the whole decision.** Every
 * other authored gate in the pack takes one — `reveal.when`, `visibleWhen`,
 * `roomVisibilityCondition`, `reachableWhen`, `handOpenCondition`,
 * `poweredWhen`, a fragment's `when`, a use interaction's, a trigger's, and
 * every conditional-text, image, ambience and pace variant. The lock was the
 * one that could not say what it meant: it took a flag id, so "shut while
 * that door is open" or "shut unless you are wearing the mask" had no route
 * at all, and the pack reached for a flag maintained in parallel with a state
 * the engine already held.
 *
 * ABSENT means locked until something unlocks it — `unlock_exit`'s ledger,
 * which is the other half of D135's pair and stays: `unlockedExitIds` is
 * history ("the bolt is cut", permanent), `lockedWhen` is a live read ("the
 * door is shut", now). An author picks the one they mean, and an exit whose
 * way back should stay open once opened wants the ledger.
 *
 * The refusal is {@link conditionalNarrativesSchema}, which is what retired
 * `keyItemId` + `lockedWithKeyItemNarratives`: "refused while carrying the
 * key" was a `has_item` gate spelled as two fields, and the general shape
 * says it as one variant while also saying every gate those two could not —
 * refused while the gas runs, refused before you have read the roster,
 * refused in the dark.
 *
 * **`blockedWhen` is the lock's second hold (D233; `actors.md` §5).** A lock
 * has a BOLT and may have an OBSTRUCTION. The bolt is `lockedWhen`, and the
 * unlock ledger cuts it for good. The obstruction is `blockedWhen`: a
 * condition that holds the way shut for exactly as long as it reads true,
 * and nothing in the ledger clears it — a body standing in a doorway is not
 * undone by the bolt having been cut last week. A lock that declares only an
 * obstruction has no bolt: it is shut while the condition holds and open
 * otherwise, with nothing to unlock. An actor's `blocksExits` is sugar the
 * loader rewrites into this arm as `blockedWhen: { actor_present }`, so a
 * door the guard stands in front of and a door that needs a keycard are the
 * same lock with different keys — and a door that needs both declares both.
 * The one `refusal` speaks for either hold; its variants choose the words.
 */
export const exitLockSchema = z
	.strictObject({
		blockedWhen: conditionSchema.optional(),
		lockedWhen: conditionSchema.optional(),
		refusal: conditionalNarrativesSchema,
	})
	.readonly();

/** One exit lock — see {@link exitLockSchema}. */
export type ExitLock = z.infer<typeof exitLockSchema>;

/**
 * One exit: a direction token, a destination, and an optional lock. The
 * destination is EXACTLY ONE of `targetRoomId` (a declared room) or
 * `leadsOutOfPack: true` (decision D19, R4): every slice has a one-way door
 * out of itself, and an outward exit is authorable truth — never traversable
 * in v0 regardless of lock state (fails safe), never a phantom room, never a
 * self-target lie on the map. Forgetting both, or declaring both, is a loud
 * schema error.
 */
export const exitDefinitionSchema = z
	.strictObject({
		/**
		 * D227 (E21, Calder's `the-road-climbs` §1): the OTHER direction words
		 * this exit answers to beside its `directionToken` — `['up']` on a road
		 * that climbs north, `['in']` on a doorway that is also north. One exit,
		 * one edge: the map reads `directionToken` alone and draws the way
		 * once, while movement (`up`, `go up`, `climb`), `go up the road`,
		 * destination resolution and the prose audit read every word. The
		 * alternative was a second exit to the same room, which the basic map
		 * annotated and the levelled scene drew as a floor that does not exist.
		 * Each word must be a direction's canonical token in the merged
		 * vocabulary, and no two exits in one room may answer the same word
		 * (its own `directionToken` included) — the loader refuses both.
		 * ABSENT is every exit there has ever been: one word.
		 */
		alsoAnswersTo: z.array(nonBlankStringSchema).min(1).readonly().optional(),
		/**
		 * D31: items this exit travels TOWARD — "go to the stairwell" resolves
		 * a destination-only `go` through these authored links. Optional; an
		 * unlinked destination earns the honest `noPathToThat` refusal.
		 */
		destinationItemIds: z.array(itemIdSchema).readonly().optional(),
		directionToken: nonBlankStringSchema,
		exitId: exitIdSchema,
		leadsOutOfPack: z.literal(true).optional(),
		lock: exitLockSchema.optional(),
		/**
		 * D138: an exit that does not exist for the player until `when` holds
		 * — hidden from movement (`north` is a wall), from the map (no stub, no
		 * fog, no name), and from the catalog's destination resolution; then
		 * REVEALED once, its `narratives` spoken to the player standing in the
		 * room, and permanent after (the world's `revealedExitIds` ledger is
		 * the truth, never the live condition — a door you have seen does not
		 * un-see when the breakers come back on). The room's own after-state is
		 * its conditional description, keyed on the same fact; this field is the
		 * EVENT and that is the STATE (D135's distinction, Calder's ruling).
		 * ABSENT is every exit there has ever been: present from the first turn.
		 */
		reveal: spokenWhenSchema.optional(),
		targetRoomId: roomIdSchema.optional(),
	})
	.readonly()
	/*
	 * D227: an exit's extra words are OTHER words. Repeating its own
	 * `directionToken`, or one word twice, is a declaration that says nothing,
	 * and the schema can say so without the vocabulary; whether each word IS
	 * a direction, and whether a sibling exit already answers it, needs the
	 * merged vocabulary and the room, so the loader judges those.
	 */
	.refine(
		(exit) =>
			exit.alsoAnswersTo === undefined ||
			(new Set(exit.alsoAnswersTo).size === exit.alsoAnswersTo.length && !exit.alsoAnswersTo.includes(exit.directionToken)),
		{
			message: 'alsoAnswersTo lists the OTHER words this exit answers to — each once, and never its own directionToken',
			path: ['alsoAnswersTo'],
		},
	)
	.refine((exit) => (exit.targetRoomId === undefined) !== (exit.leadsOutOfPack === undefined), {
		message: 'An exit must declare exactly one destination: a targetRoomId, or leadsOutOfPack: true',
		path: ['targetRoomId'],
	})
	/*
	 * D156: an outward exit refuses BEFORE its lock is engaged (D19a, fail
	 * safe), so a `lockedWhen` on one is a condition nothing will ever
	 * evaluate. The lock itself stays legal and useful — its refusal is how
	 * an outward door speaks in its own voice instead of the generic line.
	 * Found by measuring: the POC's only `lockedFlag` sat on exactly this
	 * shape and had never once executed.
	 */
	.refine((exit) => exit.leadsOutOfPack === undefined || exit.lock?.lockedWhen === undefined, {
		message: 'An outward exit refuses before its lock is engaged, so lockedWhen on it can never be evaluated — drop the condition and keep the refusal',
		path: ['lock', 'lockedWhen'],
	})
	// D233: the same truth for the obstruction — an outward door is refused before anything stands in it.
	.refine((exit) => exit.leadsOutOfPack === undefined || exit.lock?.blockedWhen === undefined, {
		message: 'An outward exit refuses before its lock is engaged, so blockedWhen on it can never be evaluated — drop the condition and keep the refusal',
		path: ['lock', 'blockedWhen'],
	});

/** One authored exit — see {@link exitDefinitionSchema}. */
export type ExitDefinition = z.infer<typeof exitDefinitionSchema>;

/**
 * One authored room: repeatable description, optional brief re-entry form,
 * one-shot first-visit beats, item placements, and exits.
 */
/**
 * Who may stand in a room (D165, platform.md §3.2): `paid` marks the part
 * of a game past the demo. Absent is `free` — every room before D165. The
 * platform enforces it on a `server` game by refusing the turn that would
 * enter a paid room without an entitlement, with the pack's own
 * `paywall` line; on a `client` game the pack ships whole, so the marking is
 * advisory and the loader says so.
 */
export const roomAccessSchema = z.enum(['free', 'paid']);

export type RoomAccess = z.infer<typeof roomAccessSchema>;

/**
 * What kind of volume the hologram map draws for a room (D169) — a fact
 * about the fiction's architecture, never about the engine's grid: a
 * `corridor` is drawn long and narrow along its links, a `chamber` wide and
 * tall, a `shaft` tall and thin (a stairwell, a lift), and a `room` is the
 * plain box every room is without saying. The flat map ignores it.
 */
export const mapRoomKindSchema = z.enum(['chamber', 'corridor', 'room', 'shaft']);

export type MapRoomKind = z.infer<typeof mapRoomKindSchema>;

/** A room's say in how the map draws it (D169). */
export const roomMapSchema = z
	.strictObject({
		kind: mapRoomKindSchema.optional(),
	})
	.readonly();

export type RoomMap = z.infer<typeof roomMapSchema>;

export const roomDefinitionSchema = z
	.strictObject({
		/** D165: `paid` past the demo; absent is free — see {@link roomAccessSchema}. */
		access: roomAccessSchema.optional(),
		/*
		 * R18-3 (D82, Calder's contract): the RE-ENTRY form — a re-orientation,
		 * not a shorter description. Spoken on arrival at an already-visited
		 * room; `look` and first entry ALWAYS speak the full `description` (the
		 * escape hatch is what makes the compression safe). Conditional text,
		 * not a string, by his ruling: a summary that can contradict the room it
		 * summarizes — "running, the bank live" to a player standing in the dark
		 * — is worse than no summary. ABSENCE is today's behavior (full text on
		 * every entry), so the field is opt-in per room and pre-R18 packs read
		 * unchanged.
		 */
		/*
		 * R35 (D115): the room's ambience OVERRIDE — wholesale when present
		 * (its own fallback, its own variants); ABSENT means inherit the pack's
		 * declaration, which is a different fact from `'silence'` (quiet on
		 * purpose — Calder's §6.2 rule). Declared on the room and never inside
		 * a scene (§8.1): audio is its own presentation axis, and a
		 * text-plus-sound pack with no pictures must be authorable.
		 */
		ambience: conditionalAmbienceSchema.optional(),
		/**
		 * D137 (Calder's §3 question, Mike's ruling): what EURYDICE says when
		 * asked about the room itself. ABSENT is today's behavior — the D63
		 * sentence, "nothing about the room itself answers to ASK" — so no
		 * pack moves; present, `ask about the corridor` is a real answer. The
		 * item field's exact shape, on the one entity that could not carry it.
		 */
		askNarratives: z.array(narrativeLineSchema).min(1).readonly().optional(),
		briefDescription: conditionalTextSchema.optional(),
		/*
		 * D65: rooms take conditional text exactly as items do (D55) — Calder's
		 * beat 7 is a ROOM going dark, and "the cathedral is dark and the number
		 * is unchanged" is a variant keyed on the breaker flag. One primitive,
		 * both kinds of describable thing.
		 */
		description: conditionalTextSchema,
		/**
		 * D140: sentences appended to the description — long OR brief, whichever
		 * form was read — while their conditions hold, in declared order; D237:
		 * or to one form only, when the fragment names it. See
		 * {@link roomDescriptionFragmentSchema}.
		 */
		descriptionFragments: z.array(roomDescriptionFragmentSchema).readonly().default([]),
		exits: z.array(exitDefinitionSchema).readonly().default([]),
		firstVisitNarratives: z.array(narrativeLineSchema).readonly().default([]),
		/*
		 * R23 (D89): the room's establishing plate — a reference into the pack's
		 * `images` table, optionally conditional in the `conditionalTextSchema`
		 * shape (identity changes only, by the D89 editorial line). ABSENCE is
		 * today's behavior: a room without a plate renders exactly as it always
		 * has, the same opt-in posture `briefDescription` took in D82.
		 */
		image: conditionalImageSchema.optional(),
		/**
		 * D142 sugar: this room IS a checkpoint — every executed turn spent in
		 * it re-takes the world's checkpoint, so a `restartAt: 'checkpoint'`
		 * ending returns the player here, to the room as they last left it.
		 * The loader desugars it into a repeatable trigger firing the
		 * `checkpoint` effect (`trigger_checkpoint_<roomId>`), so there is one
		 * primitive and the sugar is visibly sugar in the loaded bundle.
		 */
		isCheckpoint: z.literal(true).optional(),
		itemPlacements: z.array(itemIdSchema).readonly().default([]),
		/** D169: how the hologram map draws this room; absent, a plain box. */
		map: roomMapSchema.optional(),
		name: nonBlankStringSchema,
		/**
		 * D137 (Calder's 2.3): the words this room answers to BESIDE the
		 * engine's fixed room nouns (`room`, `here`, `area`, `place`,
		 * `surroundings`, `space`) and its own name — merged, never replacing.
		 * Without it a room called "Reactor Substation" could not answer
		 * `substation`, and a pack's only lever was to rename the room after
		 * the word players type. The same field items have.
		 */
		nouns: z.array(nonBlankStringSchema).readonly().default([]),
		roomId: roomIdSchema,
		/**
		 * R32 (D96): the sprites composited over this room's plate — references
		 * into the pack's `sprites` table, in stacking order (later entries
		 * render above earlier ones). Meaningful only for rooms with a plate;
		 * the loader refuses the binding otherwise (a sprite with no ground).
		 */
		sprites: z.array(spriteIdSchema).readonly().default([]),
		/** D231: the room's fallback when it is addressed about a subject none of its topics answers — the item field's shape, see `items.ts`. */
		topicFallbackNarratives: z.array(narrativeLineSchema).min(1).readonly().optional(),
		/**
		 * D231: the room's keyed answer table — `ask about the draw` where the
		 * player stands, answered by the place itself (D137 gave the room one
		 * answer, about itself; this gives it one per subject). The item
		 * field's exact shape, see `topics.ts`.
		 */
		topics: topicsSchema.optional(),
	})
	.readonly();

/** One authored room — see {@link roomDefinitionSchema}. */
export type RoomDefinition = z.infer<typeof roomDefinitionSchema>;
